import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { store } from 'store';
import { fetchUpdateUserConfig } from 'store/slices/userConfig';
import type { ApplicationUser } from 'types/company/applicationUser';
import ColoredLetterAvatar from 'ui-component/display/ColoredLetterAvatar';
import { useSelector } from 'store';
import {
  DEFAULT_CHAT_SIDEBAR_VIEW_TITLE_IDS,
  isDefaultChatSidebarViewId,
  isTeamChatSidebarViewId,
} from 'utils/default-chat-sidebar-views';
import { TEAM_CHAT_VIEW_BY_ID } from 'utils/chat-sidebar-team-view-definitions';
import ViewVisibilitySwitchWithHint from './ViewVisibilitySwitchWithHint';

type DefaultChatViewVisibilityDialogProps = {
  viewId: string;
  onClose: () => void;
};

const DefaultChatViewVisibilityDialog: React.FC<DefaultChatViewVisibilityDialogProps> = ({ viewId, onClose }) => {
  const intl = useIntl();
  const [saveHintByUdid, setSaveHintByUdid] = React.useState<Record<string, true>>({});
  const applicationUsers = useSelector((state) => state.applicationUsers);
  const hiddenMap = useSelector((state) => state.userConfig.defaultChatViewHiddenForApplicationUserUdids);

  const titleId = isDefaultChatSidebarViewId(viewId)
    ? DEFAULT_CHAT_SIDEBAR_VIEW_TITLE_IDS[viewId]
    : isTeamChatSidebarViewId(viewId)
      ? TEAM_CHAT_VIEW_BY_ID[viewId]?.title ?? null
      : null;

  const sortedUsers = React.useMemo(() => {
    return [...applicationUsers]
      .filter((u) => u.Udid)
      .sort((a, b) => (a.FullName || a.UserName || '').localeCompare(b.FullName || b.UserName || '', undefined, {
        sensitivity: 'base',
      }));
  }, [applicationUsers]);

  const setHiddenForUser = React.useCallback(
    async (udid: string, visibleToUser: boolean) => {
      const current = store.getState().userConfig.defaultChatViewHiddenForApplicationUserUdids ?? {};
      const prevList = current[viewId] ?? [];
      const hidden = new Set(prevList);
      if (visibleToUser) hidden.delete(udid);
      else hidden.add(udid);
      const nextArr = [...hidden];
      const next: Record<string, string[]> = { ...current };
      if (nextArr.length === 0) delete next[viewId];
      else next[viewId] = nextArr;
      const ok = await fetchUpdateUserConfig({ defaultChatViewHiddenForApplicationUserUdids: next }, false);
      if (ok) {
        setSaveHintByUdid((f) => ({ ...f, [udid]: true }));
        window.setTimeout(() => {
          setSaveHintByUdid((f) => {
            const n = { ...f };
            delete n[udid];
            return n;
          });
        }, 2000);
      }
    },
    [viewId],
  );

  const open = true;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h4" component="span">
            <FormattedMessage id="chat.sidebarCustomViews.visibilityDialogTitle" />
          </Typography>
          {titleId && (
            <Typography variant="body2" color="text.secondary">
              «<FormattedMessage id={titleId} />»
            </Typography>
          )}
          {!titleId && (
            <Typography variant="body2" color="text.secondary">
              {viewId}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <FormattedMessage id="chat.sidebarCustomViews.visibilityDialogSubtitle" />
        </Typography>
        <List dense disablePadding>
          {sortedUsers.map((u) => {
            const udid = u.Udid as string;
            const hidden = hiddenMap?.[viewId]?.includes(udid) ?? false;
            const label = u.FullName || u.UserName || udid;
            return (
              <ListItem
                key={udid}
                secondaryAction={
                  <ViewVisibilitySwitchWithHint
                    checked={!hidden}
                    onChange={(checked) => void setHiddenForUser(udid, checked)}
                    showSavedHint={Boolean(saveHintByUdid[udid])}
                    inputProps={{
                      'aria-label': intl.formatMessage({ id: 'chat.sidebarCustomViews.toggleShowView' }),
                    }}
                  />
                }
                sx={{ pr: 10, py: 0.75 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <ColoredLetterAvatar name={label} sx={{ width: 32, height: 32, fontSize: 14 }} />
                  <ListItemText primary={label} primaryTypographyProps={{ noWrap: true }} />
                </Stack>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          <FormattedMessage id="chat.sidebarCustomViews.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefaultChatViewVisibilityDialog;
